import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DashboardContext from './DashboardContext';
import i18n from '../../../i18n';
import { changeLanguage } from '../../../i18n';
import { getSettings } from '../services/settingService';

const DashboardProvider = ({ children }) => {
  // Vendor information
  const [vendorInfo, setVendorInfo] = useState({
    vendorName: '',
    logo: null,
    contactName: '',
    contactNumber: '',
    locationName: '',
    locationAddress: '',
  });

  // User information
  const [userInfo, setUserInfo] = useState({
    userName: '',
    email: '',
    role: '',
  });

  // Dashboard loading state is handled in main Dashboard component
  const isLoading = false;

  // Notification system
  const [notifications, setNotifications] = useState([]);
  
  // POS State
  const [isPosOpen, setIsPosOpen] = useState(false);
  
  // Theme toggle - Initialize from localStorage once
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('dashboardTheme');
    return savedTheme === 'dark';
  });

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'English';
  });

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Memoize refresh function to prevent unnecessary re-renders
  const refreshDashboardData = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // JWT token processing - Only run once on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const tokenData = JSON.parse(atob(token.split('.')[1]));
      console.log('JWT token data in DashboardProvider:', tokenData);
      
      // Update vendorInfo with data from token
      if (tokenData.vendor || tokenData.branch) {
        setVendorInfo(prev => ({
          ...prev,
          vendorName: tokenData.vendor || prev.vendorName,
          locationName: tokenData.branch || prev.locationName
        }));
      }
      
      // Also update userInfo if available
      if (tokenData.username || tokenData.email || tokenData.role) {
        setUserInfo(prev => ({
          ...prev,
          userName: tokenData.username || prev.userName,
          email: tokenData.email || prev.email,
          role: tokenData.role || prev.role
        }));
      }
    } catch (error) {
      console.error('Error parsing JWT token in DashboardProvider:', error);
    }
  }, []); // Empty dependency array - only run once

  // Branch change listener - optimized
  useEffect(() => {
    const handleBranchChange = () => {
      refreshDashboardData();
    };
    
    window.addEventListener('branch-changed', handleBranchChange);
    return () => {
      window.removeEventListener('branch-changed', handleBranchChange);
    };
  }, [refreshDashboardData]);

  // Language effects - optimized
  useEffect(() => {
    document.documentElement.setAttribute('lang', language.toLowerCase());
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
  }, [language]);

  // Load user language from settings - only run once
  useEffect(() => {
    const loadUserLanguage = async () => {
      try {
        const settings = await getSettings();
        if (settings.language) {
          changeLanguage(settings.language);
        }
      } catch (error) {
        console.error('Error loading user language from settings:', error);
        const stored = localStorage.getItem('posSystemSettings');
        if (stored) {
          const { language } = JSON.parse(stored);
          if (language) changeLanguage(language);
        }
      }
    };
    
    loadUserLanguage();
  }, []); // Empty dependency array

  // Theme persistence effect - optimized
  useEffect(() => {
    localStorage.setItem('dashboardTheme', isDarkMode ? 'dark' : 'light');
    document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    
    const root = document.documentElement;
    if (isDarkMode) {
      root.style.setProperty('--bg-primary', '#121212');
      root.style.setProperty('--bg-secondary', '#1e1e1e');
      root.style.setProperty('--text-primary', '#ffffff');
      root.style.setProperty('--text-secondary', '#b3b3b3');
    } else {
      root.style.setProperty('--bg-primary', '#f8f9fa');
      root.style.setProperty('--bg-secondary', '#ffffff');
      root.style.setProperty('--text-primary', '#333333');
      root.style.setProperty('--text-secondary', '#6c757d');
    }
  }, [isDarkMode]);
  
  // Memoize callback functions to prevent re-renders
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now(),
      read: false,
      timestamp: new Date(),
      ...notification,
    };
    
    setNotifications((prev) => [newNotification, ...prev]);
  }, []);
  
  const markNotificationAsRead = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  }, []);
  
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);
  
  const togglePosMode = useCallback(() => {
    setIsPosOpen((prev) => !prev);
  }, []);
  
  const toggleThemeMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      console.log('Theme toggled to:', newMode ? 'dark' : 'light');
      return newMode;
    });
  }, []);
  
  const updateVendorInfo = useCallback((newInfo) => {
    setVendorInfo((prev) => ({ ...prev, ...newInfo }));
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const dashboardContextValue = useMemo(() => ({
    vendorInfo,
    userInfo,
    isLoading,
    notifications,
    isPosOpen,
    isDarkMode,
    language,
    addNotification,
    markNotificationAsRead,
    clearNotifications,
    togglePosMode,
    toggleThemeMode,
    updateVendorInfo,
    setLanguage,
    refreshTrigger,
    refreshDashboardData
  }), [
    vendorInfo,
    userInfo,
    isLoading,
    notifications,
    isPosOpen,
    isDarkMode,
    language,
    addNotification,
    markNotificationAsRead,
    clearNotifications,
    togglePosMode,
    toggleThemeMode,
    updateVendorInfo,
    refreshTrigger,
    refreshDashboardData
  ]);

  return (
    <DashboardContext.Provider value={dashboardContextValue}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;